import { useEffect, useState } from 'react';

// Observe permission only. Never acquire coordinates or trigger an OS prompt
// on mount/focus. A grant is not proof that system GPS can currently get a fix.
export default function useDeviceLocationPermission(enabled, scope) {
  const [state, setState] = useState('unknown');
  useEffect(() => {
    let active = true;
    let sequence = 0;
    let permission;
    let changed;
    const detach = () => {
      permission?.removeEventListener?.('change', changed);
      permission = null;
    };
    const check = async () => {
      const current = ++sequence;
      detach();
      if (!enabled) { setState('unknown'); return; }
      if (!navigator.geolocation?.getCurrentPosition) { setState('unsupported'); return; }
      if (!navigator.permissions?.query) { setState('unknown'); return; }
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        if (!active || sequence !== current) return;
        permission = result;
        changed = () => {
          if (active && sequence === current) setState(['granted', 'denied', 'prompt'].includes(result.state) ? result.state : 'unknown');
        };
        changed();
        permission.addEventListener?.('change', changed);
      } catch {
        if (active && sequence === current) setState('unknown');
      }
    };
    const onReturn = () => { if (document.visibilityState !== 'hidden') check(); };
    setState('unknown');
    check();
    if (enabled) {
      window.addEventListener('focus', onReturn);
      document.addEventListener('visibilitychange', onReturn);
    }
    return () => {
      active = false;
      sequence += 1;
      detach();
      window.removeEventListener('focus', onReturn);
      document.removeEventListener('visibilitychange', onReturn);
    };
  }, [enabled, scope]);
  return state;
}
